import React from 'react';
import Img from '../wrapper/Img';

class BroodImageSlide extends React.Component {
	constructor(props) {
		super(props);

		// this.state = {};

		this.imageRef = React.createRef();
		this.timerStartTime = null;
		this.remainingTime = null;
	}

	componentDidMount() {
		const {
			curr
		} = this.props;

		// StrictMode's dev remount runs unmount → mount on the same instance;
		// without this reset the _unmounted latch permanently no-ops the load
		// handlers and rotation stalls.
		this._unmounted = false;

		if (curr) {
			this.prepareImage();
		}
	}

	componentWillUnmount() {
		this.stopTimer();
		// A fetching Image is kept alive by the browser until its request
		// settles, specifically so onload/onerror still fire. Detach the
		// handlers and drop the reference, otherwise a preload still in
		// flight at unmount later fires against this dead instance —
		// restarting timers and calling slideshow.next on a torn-down
		// parent, leaking the whole component chain (DEV-12093).
		this._unmounted = true;
		if (this._preloadImg) {
			this._preloadImg.onload = null;
			this._preloadImg.onerror = null;
			// Empty src aborts the in-flight request — detached preloads
			// otherwise download to completion in the background.
			this._preloadImg.src = '';
			this._preloadImg = null;
		}
	}

	componentDidUpdate(prevProps) {
		const {
			status,
			curr,
			next,
			done,
			slideshow
		} = this.props;

		if (prevProps.status.playing !== status.playing) {
			if (curr && status.playing) {
				// Resume from pause if we have remaining time
				if (this.remainingTime && this.remainingTime > 0) {
					this.resumeTimer();
				} else {
					this.onImageLoad();
				}
			} else {
				// Pausing - calculate remaining time
				if (this.slideTimer && this.timerStartTime) {
					const elapsed = Date.now() - this.timerStartTime;
					const {slide} = this.props;
					const duration = slide.options?.duration || 5000;
					this.remainingTime = Math.max(0, duration - elapsed);
					console.log('BROOD::IMAGE::PAUSE - Remaining time:', this.remainingTime);
				}
				this.stopTimer();
			}
		}

		// // no longer just a single slide
		// if (slideshow.state.slides.length > 1) {
		// 	if (curr && status.slideLoopCount > 0 && !this.slideTimer) {
		// 		return this.startTimer();
		// 	}
		// }

		if (prevProps.status.slideLoopCount !== status.slideLoopCount) {
			if (prevProps.status.slideLoopCount < status.slideLoopCount) {
				return this.startTimer();
			}
		}

		if (prevProps.curr !== curr) {
			if (curr) {
				this.prepareImage();
			}
		}

		if (prevProps.next !== next) {
			if (next) {
				if (!curr && next) {
					this.stopTimer();
				}
			}
		}

		if (prevProps.done !== done) {
			if (done) {
				this.stopTimer();
			}
		}
	}

	startTimer() {
		const {
			curr,
			slide,
			slideshow
		} = this.props;

		if (!curr) {
			return;
		}

		const {
			options
		} = slide;

		const {
			duration
		} = options;

		if (this.slideTimer) {
			this.stopTimer();
		}

		slideshow.broodProofOfPlayStart(slide);
		console.log('BROOD::IMAGE::START_TIMER:', slide.slideID, 'duration:', duration);

		// Track start time for pause/resume
		this.timerStartTime = Date.now();
		this.remainingTime = null;

		// Use setTimeout instead of setInterval to prevent multiple calls
		this.slideTimer = setTimeout(() => {
			console.log('BROOD::IMAGE::NEXT:', slide.slideID);
			// Only advance if we're still the current slide and slideshow is playing
			if (curr && slideshow.playing) {
				slideshow.next(slide, curr, {});
			} else {
				console.log('BROOD::IMAGE::SKIP_NEXT - No longer current or not playing');
			}
			this.timerStartTime = null;
			this.remainingTime = null;
		}, duration);
	}

	stopTimer() {
		const {
			slide
		} = this.props;

		console.log('BROOD::IMAGE::STOP_TIMER:', slide.slideID);

		clearTimeout(this.slideTimer);
		this.slideTimer = null;
	}

	resumeTimer() {
		const {
			curr,
			slide,
			slideshow
		} = this.props;

		if (!curr || !this.remainingTime || this.remainingTime <= 0) {
			return;
		}

		console.log('BROOD::IMAGE::RESUME_TIMER:', slide.slideID, 'remaining:', this.remainingTime);

		// Track resume time
		this.timerStartTime = Date.now();

		this.slideTimer = setTimeout(() => {
			console.log('BROOD::IMAGE::NEXT (resumed):', slide.slideID);
			if (curr && slideshow.playing) {
				slideshow.next(slide, curr, {});
			} else {
				console.log('BROOD::IMAGE::SKIP_NEXT - No longer current or not playing');
			}
			this.timerStartTime = null;
			this.remainingTime = null;
		}, this.remainingTime);
	}

	prepareImage() {
		const {
			slide
		} = this.props;

		const {
			data
		} = slide;

		// Fresh attempt — clear any errHandled from a previous mount/preload.
		this.errHandled = false;

		// Attach handlers BEFORE assigning src. Otherwise cached/synchronously-
		// failing loads (e.g. CSP block, adblock refusal, browser-cached 404)
		// can dispatch onerror/onload before the handlers are wired, silently
		// swallowing the event. Result: slideshow.next({error}) never fires,
		// addToBackoff never runs, and the broken slide gets re-picked every
		// rotation cycle while the duration timer ticks out a full slot.
		// Detach any earlier preload still in flight (slide rotated back to
		// current) so its late events can't advance the slideshow for a stale
		// attempt.
		if (this._preloadImg) {
			this._preloadImg.onload = null;
			this._preloadImg.onerror = null;
			// Abort the stale request too, not just silence its events.
			this._preloadImg.src = '';
		}
		const img = new Image();
		this._preloadImg = img;
		img.onload = this.onImageLoad.bind(this);
		img.onerror = this.onImageError.bind(this);
		img.src = data.src;
	}

	onImageLoad(e) {
		if (this._unmounted) return;
		this.startTimer();
	}

	onImageError(err) {
		if (this._unmounted) return;
		// Guard against double-fire — both the prepareImage preload and the
		// rendered <Img> wrapper can route through here. Once we've called
		// slideshow.next({error}) for this slide, additional error events
		// (e.g. wrapper-level after webp→png both fail) shouldn't trigger
		// another advance. errHandled is reset in prepareImage (next fresh
		// attempt) and on unmount via React's own lifecycle.
		if (this.errHandled) return;
		this.errHandled = true;

		const {
			slideshow,
			slide,
			curr
		} = this.props;

		this.startTimer();

		console.error('BROOD::IMAGE::ERROR:', err);

		slideshow.next(slide, curr, {
			error: err
		});
	}

	render() {
		const {
			slide,
			className: slideClassName,
			slideshow
		} = this.props;

		const {
			data
		} = slide;

		return (
			<Img
				className={slideClassName}
				style={this.props.style || {}}
				ref={this.imageRef}
				data-limit-skip={slideshow.skipper(slide)}
				data-desc={slide.description}
				src={data.src}
				alt={data.alt}
				convertFormat={data.convertFormat}
				// The Img wrapper handles webp→original fallback internally; it only
				// calls this onError prop AFTER both have failed (so we don't double-
				// fire alongside the prepareImage preload). Wiring it here ensures the
				// displayed-image path also escalates to backoff/next when both
				// formats are unreachable (e.g., adblock matching the whole URL pattern).
				onError={this.onImageError.bind(this)}
			/>
		);
	}
}

export default BroodImageSlide; 